import { fetcher } from "@/lib/fetcher";
import { ApiResponse } from "./model";
import { revalidateMcpsCache } from "./mcps-cache";
import type { MCPConfig } from "./schemas/mcp-schemas";
import type {
  MCP,
  MCPPayload,
  MCPUpdatePayload,
} from "./schemas/mcps-schemas";

export type { MCP } from "./schemas/mcps-schemas";

// MCP server CRUD against the dedicated /v1/mcp entity endpoint. MCP no
// longer rides through system-service (it is no longer a System with
// kind === "mcp"). This mirrors system-service.ts conventions: fetcher + the
// shared ApiResponse<T[]> wrapper + cache revalidation on writes.
//
// WIRE NOTE: these CRUD calls use `mcp_id` (the MCP entity id). The MCP RUNTIME
// endpoints (connect/discover/resources/subscribe in mcp-service.ts) and the
// per-entity catalog clients (mcp-{tools,prompts,resources}-service.ts) also
// take `mcp_id` on the wire.
// Only the /v1/mcp/oauth-callback response + its MCP_OAUTH_COMPLETE postMessage,
// and the separate /v1/auth-config plane, still carry this value as `system_id`.

const MCPS_URL = "/api/ai-transform/v1/mcp";

export async function createMcp(
  payload: MCPPayload,
): Promise<MCP> {
  const response = await fetcher<ApiResponse<MCP[]>>(MCPS_URL, {
    method: "POST",
    body: JSON.stringify(payload),
  });

  if (response.status === "OK" && response.data?.[0]) {
    await revalidateMcpsCache(response.data[0].mcp_id);
    return response.data[0];
  }

  throw new Error(response.message || "Failed to create MCP server");
}

export async function updateMcp(
  serverId: string,
  updates: MCPUpdatePayload,
): Promise<MCP> {
  const response = await fetcher<ApiResponse<MCP[]>>(
    `${MCPS_URL}?mcp_id=${encodeURIComponent(serverId)}`,
    {
      method: "PUT",
      body: JSON.stringify(updates),
    },
  );

  if (response.status === "OK" && response.data?.[0]) {
    await revalidateMcpsCache(serverId);
    return response.data[0];
  }

  throw new Error(response.message || "Failed to update MCP server");
}

// Convenience: update only the MCP config block (transport / headers / tool
// policy) without touching name/description. Mirrors the old updateSystemMCP
// helper that this replaces.
export async function updateMcpConfig(
  serverId: string,
  config: MCPConfig,
): Promise<MCP> {
  return updateMcp(serverId, { config });
}

export async function deleteMcp(serverId: string): Promise<boolean> {
  const response = await fetcher<ApiResponse<unknown>>(
    `${MCPS_URL}?mcp_id=${encodeURIComponent(serverId)}`,
    {
      method: "DELETE",
    },
  );

  if (response.status === "OK") {
    void revalidateMcpsCache(serverId).catch(() => undefined);
    return true;
  }

  if (response.message) {
    throw new Error(response.message);
  }

  return false;
}

// Flip a connector's lifecycle status (active|inactive) via the DEDICATED
// switch-status endpoint (PUT /v1/mcp/switch-status) — separate from updateMcp
// so end-user-visibility toggling can be authorized on its own route. PUT
// /v1/mcp no longer mutates status, so this is the only path that changes it.
export async function switchMcpStatus(
  serverId: string,
  status: "active" | "inactive",
): Promise<MCP> {
  const response = await fetcher<ApiResponse<MCP[]>>(
    `${MCPS_URL}/switch-status`,
    {
      method: "PUT",
      body: JSON.stringify({ mcp_id: serverId, status }),
    },
  );

  if (response.status === "OK" && response.data?.[0]) {
    void revalidateMcpsCache(serverId).catch(() => undefined);
    return response.data[0];
  }

  throw new Error(response.message || "Failed to switch MCP status");
}
